// API配置文件
import axios from "axios";
import { Message } from "element-ui";
import { config } from "@/config/env";

// API基础配置
const API_CONFIG = {
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
};

// 创建axios实例
const apiClient = axios.create(API_CONFIG);
apiClient.defaults.withCredentials = true; // 允许跨域携带Cookie

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 添加token到请求头
    const token = localStorage.getItem("userToken");
    if (token) {
      // FastAdmin期望的是HTTP_TOKEN头，不是Authorization
      config.headers.Token = token;

      // 获取用户信息
      const userInfoStr = localStorage.getItem("userInfo");
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo.username) {
            config.headers.Username = userInfo.username;
            console.log("✅ 已添加Token头:", token.substring(0, 10) + "...");
            console.log("✅ 已添加Username头:", userInfo.username);
          }
          
          // 自动添加 teacher_id 到请求参数（如果用户信息中有 id 或 teacher_id）
          const teacherId = userInfo.teacher_id || userInfo.id;
          if (teacherId) {
            if (config.method === "get") {
              config.params = {
                ...config.params,
                teacher_id: teacherId,
              };
            } else if (config.method === "post" || config.method === "put") {
              // 对于 POST/PUT 请求，添加到 data 中
              if (config.data) {
                if (typeof config.data === 'string') {
                  // 如果 data 是字符串（已序列化），需要先解析
                  try {
                    const parsedData = JSON.parse(config.data);
                    parsedData.teacher_id = teacherId;
                    config.data = JSON.stringify(parsedData);
                  } catch (e) {
                    console.warn("⚠️ 解析请求数据失败:", e);
                  }
                } else {
                  // 如果 data 是对象
                  config.data.teacher_id = teacherId;
                }
              }
            }
            console.log("✅ 已添加teacher_id:", teacherId);
          }
        } catch (e) {
          console.warn("⚠️ 解析用户信息失败:", e);
        }
      }
    }

    // 添加时间戳防止缓存
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error) => {
    console.error("请求配置错误:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    const { data } = response;

    // 检查是否有 success 字段（优先）
    if (data.success !== undefined) {
      if (data.success === true) {
        // 成功情况
        return data.data || data;
      } else {
        // success === false 表示失败
        const errorMessage = data.msg || data.message || "请求失败";
        const error = new Error(errorMessage);
        error.apiResponse = data;
        error.isApiError = true;
        return Promise.reject(error);
      }
    }

    // 统一处理后端返回格式（检查 code 字段）
    if (data.code !== undefined) {
      // code === 1 或 code === 200 表示成功
      if (data.code === 200) {
        // 成功情况，返回 data 字段
        return data.data || data;
      } else if (data.code === 0) {
        // code === 0 表示失败
        const errorMessage = data.msg || data.message || "请求失败";
        const error = new Error(errorMessage);
        error.apiResponse = data;
        error.isApiError = true;
        return Promise.reject(error);
      } else {
        // 其他 code，根据具体情况判断
        // 一般来说 code > 0 表示成功
        if (data.code > 0) {
          return data.data || data;
        } else {
          const errorMessage = data.msg || data.message || "请求失败";
          const error = new Error(errorMessage);
          error.apiResponse = data;
          error.isApiError = true;
          return Promise.reject(error);
        }
      }
    }

    return data;
  },
  (error) => {
    console.error("API请求错误:", error);

    // 如果是API业务错误(code=0)，不在拦截器中显示消息
    if (error.isApiError) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      let message = "请求失败";

      switch (status) {
        case 400:
          message = "请求参数错误";
          break;
        case 401:
          message = "未授权访问";
          localStorage.clear();
          window.location.href = "/login";
          break;
        case 403:
          message = "访问被禁止";
          break;
        case 404:
          message = "请求的资源不存在";
          break;
        case 500:
          message = "服务器内部错误";
          break;
        case 502:
          message = "网关错误";
          break;
        case 503:
          message = "服务不可用";
          break;
        default:
          message = data?.message || `请求失败 (${status})`;
      }

      Message.error(message);
    } else if (error.request) {
      Message.error("网络连接失败，请检查网络设置");
    } else {
      Message.error("请求配置错误");
    }

    return Promise.reject(error);
  }
);

// API端点配置
export const API_ENDPOINTS = {
  // 用户相关
  USER: {
    LOGIN: "/user/login",
    LOGOUT: "/user/logout",
    REGISTER: "/user/register",
    PROFILE: "/user/profile",
    UPDATE_PROFILE: "/user/profile",
    RESET_PASSWORD: "/user/changepwd",
  },

  // 系统相关
  SYSTEM: {
    GET_CONFIG: "/system/config",
    UPLOAD_FILE: "/system/upload",
    GET_DICTIONARY: "/system/dictionary",
  },

  // 家长端相关 (Parent Dashboard)
  PARENT: {
    STUDENT_LIST: "/parent/students", // 获取家长下的学员列表
    STUDENT_OVERVIEW: "/parent/students/overview", // 单个学员概览 ?studentId=xxx
    COURSES: "/parent/courses", // 订阅课程列表 ?studentId=xxx
    DAILY_PROGRESS: "/parent/progress/daily", // 最近N日学习数量趋势 ?studentId=xxx&range=7
    TIME_USAGE: "/parent/progress/time", // 学习时长（识别/拼写） ?studentId=xxx&range=7
    TEST_RESULTS: "/parent/tests", // 测试成绩列表 ?studentId=xxx
    REVIEW_STATUS: "/parent/review/status", // 待复习/过期统计 ?studentId=xxx
    SETTINGS_GET: "/parent/settings/get", // 获取当前学习策略 ?studentId=xxx
    SETTINGS_UPDATE: "/parent/settings/update", // 更新策略 POST
    ACHIEVEMENTS: "/parent/achievements", // 成就/奖励数据 ?studentId=xxx
  },
};

export default apiClient;
